import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  type Cell,
  type Row,
  type SortingState,
  type Table,
} from "@tanstack/react-table";
import { useLayoutEffect, useRef, type ReactNode } from "react";

import { LoadingSpinner } from "@/components/ui";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { cn } from "@/lib/utils/cn";
import { DataTablePagination } from "../DataTable/DataTablePagination";

import { DraggableCardTableColumnHeader } from "./DraggableCardTableColumnHeader";
import { DraggableColumnTableCell } from "./DraggableColumnTableCell";

// Keeps vertical auto-scroll effectively disabled while allowing horizontal edge scrolling.
const HEADER_DRAG_AUTO_SCROLL = { threshold: { x: 0.2, y: 0.001 } };

export type DraggableCardTableProps<TData> = {
  table: Table<TData>;
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  /** Left side of the muted toolbar row (e.g. search). */
  toolbarLeading?: ReactNode;
  /** Right side of the toolbar row (e.g. visibility menu). */
  toolbarTrailing?: ReactNode;
  /** Rendered after the toolbar row and before the horizontal scroll region. */
  belowToolbar?: ReactNode;
  showToolbar?: boolean;
  onRowClick?: (row: Row<TData>) => void;
  renderContextMenu?: (row: Row<TData>, trigger: ReactNode) => ReactNode;
  getCellClassName?: (cell: Cell<TData, unknown>) => string | undefined;
  showPaginationFooter?: boolean;
  entityLabelPlural?: string;
  pageSizeOptions?: readonly number[];
  isServerPaginated?: boolean;
  serverRowCount?: number;
  serverCanNextPage?: boolean;
  /** Locks column widths and truncates cell content instead of reflowing on new data. */
  fixedColumnWidths?: boolean;
  /**
   * Pass the same `sorting` / `columnOrder` state you feed into `useReactTable` so horizontal
   * scroll is restored after sort or column reorder without effect deps firing every render.
   */
  scrollRestoreSorting?: SortingState;
  scrollRestoreColumnOrder?: string[];
  tableTestId?: string;
  rowClassName?: string;
  footerClassName?: string;
  /** Defaults to updating column order via `table.setColumnOrder`. */
  onColumnDragEnd?: (event: DragEndEvent) => void;
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
};

export function DraggableCardTable<TData>({
  table,
  isLoading,
  isFetching = false,
  isError,
  errorMessage = serverErrorFallbackText,
  emptyMessage = "No results.",
  toolbarLeading,
  toolbarTrailing,
  belowToolbar,
  showToolbar = true,
  onRowClick,
  renderContextMenu,
  getCellClassName,
  showPaginationFooter = true,
  entityLabelPlural = "rows",
  pageSizeOptions = [10, 25, 50, 100],
  isServerPaginated = false,
  serverRowCount,
  serverCanNextPage,
  fixedColumnWidths = false,
  scrollRestoreSorting,
  scrollRestoreColumnOrder,
  tableTestId,
  rowClassName = "border-border hover:bg-primary/5 cursor-pointer border-b",
  footerClassName = "text-foreground/80",
  onColumnDragEnd,
  scrollableBody = false,
}: DraggableCardTableProps<TData>) {
  const columnOrder = table.getState().columnOrder;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollLeftRef = useRef(0);
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (el) {
      el.scrollLeft = tableScrollLeftRef.current;
    }
  }, [scrollRestoreSorting, scrollRestoreColumnOrder]);

  const handleColumnDragEnd =
    onColumnDragEnd ??
    ((event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      table.setColumnOrder((order) => {
        const oldIndex = order.indexOf(active.id as string);
        const newIndex = order.indexOf(over.id as string);
        if (oldIndex < 0 || newIndex < 0) return order;
        return arrayMove(order, oldIndex, newIndex);
      });
    });

  const tableHeaders = table.getHeaderGroups()[0].headers;
  const visibleColumnCount = table.getVisibleLeafColumns().length;

  const loadingBodyHeight = Math.max(
    112,
    table.getState().pagination.pageSize * 40,
  );

  return (
    <>
      {showToolbar ? (
        <div className="border-border bg-muted/30 flex min-w-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">{toolbarLeading}</div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {toolbarTrailing}
          </div>
        </div>
      ) : null}

      {belowToolbar}

      <div
        className={cn(
          "relative",
          scrollableBody && "flex min-h-0 flex-1 flex-col",
        )}
      >
        {isFetching && !isLoading && (
          <div className="bg-background/75 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
            <LoadingSpinner />
          </div>
        )}
        <div
          ref={tableScrollRef}
          onScroll={(event) => {
            tableScrollLeftRef.current = event.currentTarget.scrollLeft;
          }}
          className={cn(
            "w-full min-w-0 overflow-x-auto overscroll-x-contain",
            scrollableBody &&
              "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          )}
        >
          <DndContext
            autoScroll={HEADER_DRAG_AUTO_SCROLL}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleColumnDragEnd}
            sensors={sensors}
          >
            <table
              className={cn(
                "text-foreground min-w-full text-xs leading-4",
                fixedColumnWidths ? "table-fixed" : "w-max",
              )}
              style={
                fixedColumnWidths ? { width: table.getTotalSize() } : undefined
              }
              data-testid={tableTestId}
            >
              <thead className="bg-muted/40 relative z-20">
                <tr className="border-border border-b">
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {tableHeaders.map((header) => (
                      <DraggableCardTableColumnHeader
                        key={header.id}
                        header={header}
                        fixedColumnWidths={fixedColumnWidths}
                        sticky={scrollableBody}
                      />
                    ))}
                  </SortableContext>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={Math.max(1, visibleColumnCount)}
                      className="p-0 align-middle"
                    >
                      <div
                        className="sticky left-0 flex w-screen items-center justify-center px-4 py-10"
                        style={{ minHeight: loadingBodyHeight }}
                      >
                        <LoadingSpinner />
                      </div>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td
                      colSpan={Math.max(1, visibleColumnCount)}
                      className="text-destructive px-4 py-8 text-center"
                    >
                      {errorMessage}
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={Math.max(1, visibleColumnCount)}
                      className="p-0 align-middle"
                    >
                      <div className="text-foreground/70 sticky left-0 flex min-h-[7rem] w-screen items-center justify-center px-4 py-8 text-center">
                        {emptyMessage}
                      </div>
                    </td>
                  </tr>
                ) : (
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {table.getRowModel().rows.map((row) => {
                      const trigger = (
                        <tr
                          key={row.id}
                          className={cn(rowClassName)}
                          onClick={
                            onRowClick
                              ? () => {
                                  onRowClick(row);
                                }
                              : undefined
                          }
                        >
                          {row.getVisibleCells().map((cell) => (
                            <DraggableColumnTableCell
                              key={cell.id}
                              cell={cell}
                              fixedColumnWidths={fixedColumnWidths}
                              className={cn(
                                "py-3 pr-4 pl-9 align-middle whitespace-nowrap",
                                getCellClassName?.(cell),
                              )}
                            />
                          ))}
                        </tr>
                      );

                      return renderContextMenu
                        ? renderContextMenu(row, trigger)
                        : trigger;
                    })}
                  </SortableContext>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>

      {showPaginationFooter ? (
        <div
          className={cn(
            "border-border shrink-0 border-t px-4 py-3",
            footerClassName,
          )}
        >
          <DataTablePagination
            table={table}
            entityLabelPlural={entityLabelPlural}
            pageSizeOptions={pageSizeOptions}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            isServerPaginated={isServerPaginated}
            serverRowCount={serverRowCount}
            serverCanNextPage={serverCanNextPage}
          />
        </div>
      ) : null}
    </>
  );
}
